import validateEmail from "@insite/client-framework/Common/Utilities/validateEmail";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import setFieldValue from "@insite/client-framework/Store/Components/ContactUsForm/Handlers/SetFieldValue";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { ContactFormContext } from "@insite/content-library/Widgets/ContactUs/ContactForm";
import TextField, { TextFieldPresentationProps } from "@insite/mobius/TextField";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";

const enum fields {
    label = "label",
    hintText = "hintText",
    isRequired = "isRequired",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.label]: string;
        [fields.hintText]: string;
        [fields.isRequired]: boolean;
    };
}

const mapStateToProps = (state: ApplicationState) => ({
    website: state.components.contactUsForm.fieldValues["website"],
});

const mapDispatchToProps = {
    setFieldValue,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface WebsiteStyles {
    websiteTextField?: TextFieldPresentationProps;
}

export const websiteStyles: WebsiteStyles = {};

const styles = websiteStyles;

const Website: React.FC<Props> = ({ fields, website, setFieldValue }) => {
    const { label, hintText, isRequired } = fields;
    const contactFormContext = React.useContext(ContactFormContext);
    const [websiteErrorMessage, setWebsiteErrorMessage] = React.useState("");

    React.useEffect(() => {
        contactFormContext.validators.website = validateWebsite;
    }, [website]);

    React.useEffect(() => {
        contactFormContext.validators.website = validateWebsite;
    }, [isRequired]);

    const validateWebsite = () => {
        const errorMessage =
            !website && isRequired
                ? siteMessage("ContactUsForm_WebsiteIsRequiredErrorMessage")
                : !website || validateEmail(website)
                ? ""
                : siteMessage("ContactUsForm_WebsiteIsInvalidErrorMessage");
        setWebsiteErrorMessage(errorMessage as string);
        return !errorMessage;
    };

    const emailChangeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
        setFieldValue({ key: "website", value: event.target.value });
    };

    const emailBlurHandler = (event: React.FocusEvent<HTMLInputElement>) => {
        validateWebsite();
    };

    return (
        <TextField
            {...styles.websiteTextField}
            label={label}
            required={isRequired}
            placeholder={hintText}
            value={website || ""}
            onChange={emailChangeHandler}
            onBlur={emailBlurHandler}
            error={websiteErrorMessage}
        />
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(Website),
    definition: {
        group: "Contact Us",
        icon: "FormField",
        fieldDefinitions: [
            {
                name: fields.label,
                displayName: "Label",
                editorTemplate: "TextField",
                fieldType: "Translatable",
                defaultValue: "Website",
                isRequired: true,
            },
            {
                name: fields.hintText,
                displayName: "Hint Text",
                editorTemplate: "TextField",
                fieldType: "Translatable",
                defaultValue: "Enter Website",
                isRequired: true,
            },
            {
                name: fields.isRequired,
                displayName: "Required Field",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
            },
        ],
    },
};

export default widgetModule;
